import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, X, Play, Pause, Maximize2, Minimize2, RotateCcw, CheckCircle } from 'lucide-react';

const TrainingPresentation = ({ slides, title, onComplete, onExit }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isAutoPlaying, setIsAutoPlaying] = useState(false);
  const [direction, setDirection] = useState('next');
  const [completedSlides, setCompletedSlides] = useState(new Set());

  const totalSlides = slides.length;
  const progress = ((currentSlide + 1) / totalSlides) * 100;

  // Mark current slide as viewed
  useEffect(() => {
    setCompletedSlides(prev => new Set([...prev, currentSlide]));
  }, [currentSlide]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowRight' || e.key === ' ') {
        e.preventDefault();
        goToNext();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goToPrev();
      } else if (e.key === 'Escape') {
        if (isFullscreen) {
          exitFullscreen();
        } else {
          onExit?.();
        }
      } else if (e.key === 'f' || e.key === 'F') {
        toggleFullscreen();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentSlide, isFullscreen]);

  // Auto-play functionality
  useEffect(() => {
    let interval;
    if (isAutoPlaying) {
      interval = setInterval(() => {
        if (currentSlide < totalSlides - 1) {
          goToNext();
        } else {
          setIsAutoPlaying(false);
        }
      }, 5000); // 5 seconds per slide
    }
    return () => clearInterval(interval);
  }, [isAutoPlaying, currentSlide, totalSlides]);

  const goToNext = useCallback(() => {
    if (currentSlide < totalSlides - 1) {
      setDirection('next');
      setCurrentSlide(prev => prev + 1);
    } else if (completedSlides.size === totalSlides) {
      onComplete?.();
    }
  }, [currentSlide, totalSlides, completedSlides, onComplete]);

  const goToPrev = useCallback(() => {
    if (currentSlide > 0) {
      setDirection('prev');
      setCurrentSlide(prev => prev - 1);
    }
  }, [currentSlide]);

  const goToSlide = (index) => {
    setDirection(index > currentSlide ? 'next' : 'prev');
    setCurrentSlide(index);
  };

  const toggleFullscreen = async () => {
    if (!document.fullscreenElement) {
      await document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      await document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  const exitFullscreen = async () => {
    if (document.fullscreenElement) {
      await document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  const restart = () => {
    setCurrentSlide(0);
    setCompletedSlides(new Set());
    setIsAutoPlaying(false);
  };

  const slide = slides[currentSlide];

  return (
    <div className={`fixed inset-0 bg-[#f8f9fa] z-50 flex flex-col ${isFullscreen ? 'fullscreen-mode' : ''}`}>
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 bg-white border-b border-gray-200 shadow-sm">
        <div className="flex items-center gap-4">
          <button
            onClick={onExit}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            data-testid="exit-presentation"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
          <div>
            <h1 className="text-lg font-semibold text-gray-800">{title}</h1>
            <p className="text-sm text-gray-500">Slide {currentSlide + 1} of {totalSlides}</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setIsAutoPlaying(!isAutoPlaying)}
            className={`p-2 rounded-lg transition-colors ${isAutoPlaying ? 'bg-teal-100 text-teal-600' : 'hover:bg-gray-100 text-gray-500'}`}
            title={isAutoPlaying ? 'Pause auto-play' : 'Auto-play (5s per slide)'}
          >
            {isAutoPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
          </button>
          <button
            onClick={restart}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors text-gray-500"
            title="Restart presentation"
          >
            <RotateCcw className="w-5 h-5" />
          </button>
          <button
            onClick={toggleFullscreen}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors text-gray-500"
            title="Toggle fullscreen (F)"
          >
            {isFullscreen ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Progress bar */}
      <div className="h-1 bg-gray-200">
        <div 
          className="h-full bg-gradient-to-r from-teal-500 to-teal-400 transition-all duration-500"
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* Main content */}
      <div className="flex-1 flex items-center justify-center p-8 overflow-hidden">
        <div 
          className={`w-full max-w-5xl transition-all duration-500 transform ${
            direction === 'next' ? 'slide-in-right' : 'slide-in-left'
          }`}
          key={currentSlide}
        >
          {/* Slide content */}
          <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-xl">
            {/* Slide header */}
            <div className={`p-8 ${slide.bgColor || 'bg-gradient-to-r from-teal-50 to-cyan-50'}`}>
              <div className="flex items-center gap-4 mb-4">
                {slide.icon && (
                  <div className={`p-3 rounded-xl ${slide.iconBg || 'bg-teal-100'}`}>
                    {slide.icon}
                  </div>
                )}
                <div>
                  <span className="text-xs font-medium text-teal-600 uppercase tracking-wider">
                    {slide.category || 'Training'}
                  </span>
                  <h2 className="text-3xl font-semibold text-gray-800 mt-1">{slide.title}</h2>
                </div>
              </div>
              {slide.subtitle && (
                <p className="text-lg text-gray-600">{slide.subtitle}</p>
              )}
            </div>

            {/* Slide body */}
            <div className="p-8">
              {slide.content}
            </div>

            {/* Slide tips */}
            {slide.tips && (
              <div className="px-8 pb-8">
                <div className="bg-teal-50 border border-teal-200 rounded-xl p-4">
                  <p className="text-sm font-medium text-teal-700 mb-2">💡 Pro Tip</p>
                  <p className="text-gray-600">{slide.tips}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="px-6 py-4 bg-white border-t border-gray-200">
        <div className="flex items-center justify-between max-w-5xl mx-auto">
          {/* Prev button */}
          <button
            onClick={goToPrev}
            disabled={currentSlide === 0}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
              currentSlide === 0
                ? 'text-gray-400 cursor-not-allowed'
                : 'text-gray-600 hover:bg-gray-100 hover:text-gray-800'
            }`}
          >
            <ChevronLeft className="w-5 h-5" />
            <span>Previous</span>
          </button>

          {/* Slide indicators */}
          <div className="flex items-center gap-2">
            {slides.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`w-3 h-3 rounded-full transition-all ${
                  index === currentSlide
                    ? 'bg-teal-500 w-8'
                    : completedSlides.has(index)
                    ? 'bg-teal-300 hover:bg-teal-400'
                    : 'bg-gray-300 hover:bg-gray-400'
                }`}
              />
            ))}
          </div>

          {/* Next button */}
          <button
            onClick={goToNext}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
              currentSlide === totalSlides - 1
                ? 'bg-green-600 hover:bg-green-700 text-white'
                : 'bg-teal-600 hover:bg-teal-700 text-white'
            }`}
          >
            <span>{currentSlide === totalSlides - 1 ? 'Complete' : 'Next'}</span>
            {currentSlide === totalSlides - 1 ? (
              <CheckCircle className="w-5 h-5" />
            ) : (
              <ChevronRight className="w-5 h-5" />
            )}
          </button>
        </div>

        {/* Keyboard hints */}
        <div className="flex items-center justify-center gap-4 mt-3 text-xs text-gray-500">
          <span>← → Arrow keys to navigate</span>
          <span>•</span>
          <span>Space for next</span>
          <span>•</span>
          <span>F for fullscreen</span>
          <span>•</span>
          <span>Esc to exit</span>
        </div>
      </div>

      <style>{`
        .slide-in-right {
          animation: slideInRight 0.5s ease-out;
        }
        .slide-in-left {
          animation: slideInLeft 0.5s ease-out;
        }
        @keyframes slideInRight {
          from {
            opacity: 0;
            transform: translateX(50px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        @keyframes slideInLeft {
          from {
            opacity: 0;
            transform: translateX(-50px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
      `}</style>
    </div>
  );
};

export default TrainingPresentation;
